import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getFlagsWithEntries,
  deleteFlag,
  updateFlag,
} from "../features/flag/flagSlice";
import {
  deleteEntry,
  setEntryInvalid,
  setEntryValid,
  notifyPlayer,
} from "../features/entry/entrySlice";
import { useAccessControl } from "../context/AccessControlContext";
import FlagReviewDialog, { AiFlagBadge } from "./FlagReviewDialog";

const Flags = () => {
  const dispatch = useDispatch();
  const { isAdmin } = useAccessControl();
  const { flagsWithEntries, isLoading } = useSelector((state) => state.flag);
  const { isLoading: entryLoading } = useSelector((state) => state.entry);

  const [selectedFlagId, setSelectedFlagId] = useState(null);
  const [invalidDialog, setInvalidDialog] = useState({
    open: false,
    entry: null,
  });
  const [invalidReason, setInvalidReason] = useState("");
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingFlagId, setUpdatingFlagId] = useState(null);
  const [resolveDialog, setResolveDialog] = useState({
    open: false,
    flag: null,
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    flag: null,
  });
  const [deleteEntryDialog, setDeleteEntryDialog] = useState({
    open: false,
    entry: null,
  });

  useEffect(() => {
    dispatch(getFlagsWithEntries());
  }, [dispatch]);

  if (!isAdmin) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error">
          Access Denied: Only administrators can access this page.
        </Typography>
      </Box>
    );
  }

  const handleCloseReview = () => {
    setSelectedFlagId(null);
  };

  const handleDeleteFlag = (flag) => {
    setDeleteDialog({ open: true, flag });
  };

  const handleConfirmDeleteFlag = async () => {
    const result = await dispatch(deleteFlag(deleteDialog.flag.id));
    if (result.type === deleteFlag.fulfilled.type) {
      setDeleteDialog({ open: false, flag: null });
      // Close the review popup immediately; the reducer drops the flag from
      // flagsWithEntries so the refetch cannot reopen it.
      setSelectedFlagId(null);
      // Refetch so the deleted flag disappears from flagsWithEntries.
      dispatch(getFlagsWithEntries());
    }
  };

  const handleFlagStatus = async (flag, status) => {
    setUpdatingFlagId(flag.id);
    const result = await dispatch(updateFlag({ id: flag.id, status }));
    setUpdatingFlagId(null);
    if (result.type === updateFlag.fulfilled.type) {
      // The PATCH response has no client-computed entries array or fresh
      // traced_player, so refetch rather than merging into the reducer.
      dispatch(getFlagsWithEntries());
    }
  };

  const handleConfirmResolve = async () => {
    const flag = resolveDialog.flag;
    setResolveDialog({ open: false, flag: null });
    await handleFlagStatus(flag, "resolved");
  };

  const handleOpenInvalidDialog = (entry) => {
    setInvalidDialog({ open: true, entry });
    // Opening on an already-invalid entry edits its message: prefill it.
    setInvalidReason(entry.is_valid === false ? entry.invalid_reason || "" : "");
    setError("");
  };

  const handleCloseInvalidDialog = () => {
    setInvalidDialog({ open: false, entry: null });
    setInvalidReason("");
    setError("");
  };

  const handleMarkInvalid = async () => {
    if (!invalidReason.trim()) {
      setError("Please provide a reason for marking this entry as invalid");
      return;
    }

    const result = await dispatch(
      setEntryInvalid({
        id: invalidDialog.entry.id,
        reason: invalidReason,
        markedInvalidBy: null,
      }),
    );

    if (result.type === setEntryInvalid.fulfilled.type) {
      handleCloseInvalidDialog();
      dispatch(getFlagsWithEntries());
    } else {
      setError("Failed to mark entry as invalid");
    }
  };

  const handleMarkValid = async (entryId) => {
    const result = await dispatch(setEntryValid(entryId));

    if (result.type === setEntryValid.fulfilled.type) {
      dispatch(getFlagsWithEntries());
    }
  };

  const handleDeleteEntry = (entry) => {
    setDeleteEntryDialog({ open: true, entry });
  };

  const handleResendNotification = (entry) => {
    dispatch(notifyPlayer(entry.id));
  };

  const handleConfirmDeleteEntry = async () => {
    const result = await dispatch(deleteEntry(deleteEntryDialog.entry.id));
    if (result.type === deleteEntry.fulfilled.type) {
      setDeleteEntryDialog({ open: false, entry: null });
      dispatch(getFlagsWithEntries());
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  // Full date + time, so admins can verify exactly when an entry changed
  // (updated_at) or was marked invalid (marked_invalid_at).
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB");
  };

  const FILTERS = [
    { value: "all", label: "Open" },
    { value: "pending", label: "Pending" },
    { value: "reviewed", label: "Reviewed" },
    { value: "resolved", label: "Resolved" },
  ];

  const statusCounts = flagsWithEntries.reduce((acc, flag) => {
    const status = flag.status || "pending";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const openCount = flagsWithEntries.filter(
    (flag) => (flag.status || "pending") !== "resolved",
  ).length;

  // Resolved flags stay out of the default view - they only appear when
  // the admin explicitly picks the Resolved filter.
  const visibleFlags =
    statusFilter === "all"
      ? flagsWithEntries.filter(
          (flag) => (flag.status || "pending") !== "resolved",
        )
      : flagsWithEntries.filter(
          (flag) => (flag.status || "pending") === statusFilter,
        );

  // Derived from the full list (not the filtered view) so the popup stays
  // open when a filter hides the selected flag's row.
  const selectedFlag =
    flagsWithEntries.find((flag) => flag.id === selectedFlagId) || null;

  // Entries that already carry an unresolved flag - a duplicate candidate in
  // that state is context, not a new find.
  const openFlaggedEntryIds = new Set(
    flagsWithEntries
      .filter((flag) => (flag.status || "pending") !== "resolved")
      .flatMap((flag) => [
        flag.entry_id,
        ...(flag.entries || []).map((entry) => entry.id),
      ])
      .filter((entryId) => entryId != null)
      .map((entryId) => String(entryId)),
  );

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 700,
            fontSize: { xs: "1.3rem", sm: "1.5rem" },
          }}
        >
          🚩 Flagged Entries
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and manage flagged entries from users
        </Typography>
      </Box>

      {/* Status filter chips - counts come from the already-fetched flags */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
        {FILTERS.map((filter) => {
          const selected = statusFilter === filter.value;
          const count =
            filter.value === "all"
              ? openCount
              : statusCounts[filter.value] || 0;
          return (
            <Chip
              key={filter.value}
              label={`${filter.label} (${count})`}
              variant={selected ? "filled" : "outlined"}
              color={
                filter.value === "all"
                  ? "primary"
                  : filter.value === "pending"
                    ? "default"
                    : filter.value === "reviewed"
                      ? "warning"
                      : "success"
              }
              onClick={() => setStatusFilter(filter.value)}
            />
          );
        })}
      </Box>

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ py: 12 }}
        >
          <CircularProgress size={50} />
        </Box>
      ) : flagsWithEntries.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "background.paper",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            ✓ No flagged entries at this time
          </Typography>
        </Box>
      ) : visibleFlags.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "background.paper",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No{" "}
            {FILTERS.find((filter) => filter.value === statusFilter)?.label ||
              ""}{" "}
            flags
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table
            sx={{
              minWidth: 1000,
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              "& td, & th": { border: "none" },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Player
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Event
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Created
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Resolved
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Reason
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleFlags.map((flag) => {
                const selected = selectedFlagId === flag.id;

                return (
                  <TableRow
                    key={`flag-${flag.id}`}
                    selected={selected}
                    hover
                    onClick={() => setSelectedFlagId(flag.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        fontWeight: 500,
                      }}
                    >
                      {flag.player_name}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {flag.event_name}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {formatDate(flag.event_date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {formatDateTime(flag.created_at)}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {flag.resolved_at
                        ? formatDateTime(flag.resolved_at)
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ py: 1.25, px: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "error.main",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {flag.reason}
                        </Typography>
                        {flag.source === "ai" && <AiFlagBadge />}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.25, px: 1.5 }}>
                      <Chip
                        label={flag.status || "pending"}
                        color={
                          flag.status === "resolved"
                            ? "success"
                            : flag.status === "reviewed"
                              ? "warning"
                              : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Review popup - opens when a row is clicked */}
      {selectedFlag && (
        <FlagReviewDialog
          flag={selectedFlag}
          flagsWithEntries={flagsWithEntries}
          openFlaggedEntryIds={openFlaggedEntryIds}
          updating={updatingFlagId === selectedFlag.id}
          onClose={handleCloseReview}
          onSelectFlag={setSelectedFlagId}
          onResolve={(flag) => setResolveDialog({ open: true, flag })}
          onReopen={(flag) => handleFlagStatus(flag, "pending")}
          onMarkValid={handleMarkValid}
          onMarkInvalid={handleOpenInvalidDialog}
          onResend={handleResendNotification}
          onDeleteEntry={handleDeleteEntry}
          onDeleteFlag={handleDeleteFlag}
        />
      )}

      {/* Mark as Invalid Dialog */}
      <Dialog
        open={invalidDialog.open}
        onClose={handleCloseInvalidDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {invalidDialog.entry?.is_valid === false
            ? "Update Invalid Message"
            : "Mark Entry as Invalid"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {invalidDialog.entry?.is_valid === false
              ? "Update the reason this entry was marked invalid."
              : "Are you sure you want to mark this entry as invalid? Please provide a reason."}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for marking invalid"
            value={invalidReason}
            onChange={(e) => setInvalidReason(e.target.value)}
            placeholder="Enter the reason for marking this entry as invalid..."
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvalidDialog}>Cancel</Button>
          <Button
            onClick={handleMarkInvalid}
            variant="contained"
            color="warning"
            disabled={entryLoading}
          >
            {entryLoading
              ? "Saving..."
              : invalidDialog.entry?.is_valid === false
                ? "Update"
                : "Mark Invalid"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Resolved Dialog - fires outcome emails to the flagger and
          (when traced) the flagged player */}
      <Dialog
        open={resolveDialog.open}
        onClose={() => setResolveDialog({ open: false, flag: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Flag as Resolved</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Resolution emails will be sent
            {resolveDialog.flag?.user_email
              ? ` to ${resolveDialog.flag.user_email}`
              : " to the flagger"}
            {resolveDialog.flag?.traced_player
              ? ` and to ${resolveDialog.flag.traced_player.firstName} ${resolveDialog.flag.traced_player.lastName}`
              : ""}
            . Are you sure you want to resolve this flag?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog({ open: false, flag: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmResolve}
            variant="contained"
            color="success"
            disabled={isLoading || updatingFlagId !== null}
          >
            {isLoading ? "Resolving..." : "Mark Resolved"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Entry Dialog */}
      <Dialog
        open={deleteEntryDialog.open}
        onClose={() => setDeleteEntryDialog({ open: false, entry: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Entry</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete this entry
            {deleteEntryDialog.entry
              ? ` (${deleteEntryDialog.entry.event})`
              : ""}
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteEntryDialog({ open: false, entry: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteEntry}
            variant="contained"
            color="error"
            disabled={entryLoading}
          >
            {entryLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Flag Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, flag: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Flag</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete this flag
            {deleteDialog.flag ? ` for ${deleteDialog.flag.player_name}` : ""}?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, flag: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteFlag}
            variant="contained"
            color="error"
            disabled={isLoading}
          >
            {isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Flags;
